import { FC, memo, useCallback, useEffect, useState, useMemo } from 'react';
import { useOfferContext } from './hook/useOfferContext';
import { useRequestContext } from './hook/useRequestContext';
import { UserProvider } from '../UsersPage/context/userContext';
import { useCopyLink } from './hook/useCopyLink';

import { Box, Chip } from '@mui/material';
import AssignmentIcon from '@mui/icons-material/Assignment';
import EditIcon from '@mui/icons-material/Edit';
import LinkIcon from '@mui/icons-material/Link';
import { RequestProvider } from './context/requestContext';
import { OfferProvider } from './context/offerContext';
import { DataTable, TableColumn, TableAction, FilterConfig } from '../../common/table';
import RequestEditModal from '../RequestsPage/component/RequestEditModal';
import { RequestType } from '../../type/request/enum/RequestType';
import RequestStatus from '../../type/request/enum/RequestStatus';


interface IRequestPage {}

const RequestsPage: FC<IRequestPage> = () => {
    const { requests, getRequests } = useRequestContext();
    const { getRequestOffers } = useOfferContext();
    const [requestIdForEdit, setRequestIdForEdit] = useState<string>('');
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const { copyLink } = useCopyLink();

    const fetchRequests = useCallback(async () => {
        setLoading(true);
        try {
            await getRequests(0, 1000); // Load all for client-side pagination
        } finally {
            setLoading(false);
        }
    }, [getRequests]);

    useEffect(() => {
        fetchRequests();
    }, [fetchRequests]);

    const handleEditClick = async (row: any): Promise<void> => {
        await getRequestOffers(row.id);
        setRequestIdForEdit(row.id);
        setEditModalOpen(true);
    };

    const handleCopyLink = (row: any): void => {
        copyLink(row.id);
    };

    const handleEditModalClose = (): void => {
        setEditModalOpen(false);
    };

    const prepareTableData = () => {
        if (!requests?.items) return [];

        return requests.items.map((request: any) => ({
            id: request.id,
            clientName:
                request.type === RequestType.ANONYMOUS
                    ? 'PROMO - Client Not accepted'
                    : `${request.client?.firstName || ''} ${request.client?.lastName || ''}`.trim() || 'N/A',
            clientPhoneNumber:
                request.type === RequestType.ANONYMOUS
                    ? 'PROMO - No phone number'
                    : request.client?.phoneNumber || 'N/A',
            operatorName: request.operator
                ? `${request.operator?.firstName || ''} ${request.operator?.lastName || ''}`.trim()
                : 'Not assigned',
            operatorPhoneNumber: request.operator?.phoneNumber || 'Not in work',
            status: request.status,
            type: request.type,
            createdAt: request.createdAt,
        }));
    };

    // Get unique operators and clients for filter dropdowns
    const { operators, clients } = useMemo(() => {
        if (!requests?.items) return { operators: [], clients: [] };

        const operatorSet = new Set<string>();
        const clientSet = new Set<string>();

        requests.items.forEach((request: any) => {
            if (request.operator) {
                const operatorName = `${request.operator?.firstName || ''} ${request.operator?.lastName || ''}`.trim();
                if (operatorName) operatorSet.add(operatorName);
            }

            if (request.type !== RequestType.ANONYMOUS && request.client) {
                const clientName = `${request.client?.firstName || ''} ${request.client?.lastName || ''}`.trim();
                if (clientName) clientSet.add(clientName);
            }
        });

        return {
            operators: Array.from(operatorSet).sort().map(name => ({ label: name, value: name })),
            clients: Array.from(clientSet).sort().map(name => ({ label: name, value: name })),
        };
    }, [requests]);

    const filters: FilterConfig[] = [
        {
            field: 'status',
            label: 'Status',
            options: [
                { label: 'Created', value: RequestStatus.CREATED },
                { label: 'In Progress', value: RequestStatus.IN_PROGRESS },
                { label: 'Offer Created', value: RequestStatus.OFFER_CREATED },
                { label: 'In Delivery by Courier', value: RequestStatus.IN_DELIVERY_BY_COURIER },
                { label: 'In Delivery by Agent', value: RequestStatus.IN_DELIVERY_BY_AGENT },
                { label: 'Closed', value: RequestStatus.CLOSED },
                { label: 'Archived', value: RequestStatus.ARCHIVED },
            ],
        },
        {
            field: 'operatorName',
            label: 'Operator',
            options: operators,
        },
        {
            field: 'clientName',
            label: 'Client',
            options: clients,
        },
        {
            field: 'createdAt',
            label: 'Created Date',
            type: 'daterange' as const,
        },
    ];

    const columns: TableColumn[] = [
        {
            field: 'clientName',
            headerName: 'Client Name',
            minWidth: 150,
            flex: 1,
            sortable: true,
        },
        {
            field: 'clientPhoneNumber',
            headerName: 'Client Phone',
            width: 150,
            sortable: true,
        },
        {
            field: 'operatorName',
            headerName: 'Operator Name',
            minWidth: 150,
            flex: 1,
            sortable: true,
        },
        {
            field: 'operatorPhoneNumber',
            headerName: 'Operator Phone',
            width: 150,
            sortable: true,
        },
        {
            field: 'status',
            headerName: 'Status',
            width: 180,
            sortable: true,
            renderCell: (value) => {
                const statusColors: Record<string, 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info'> = {
                    CREATED: 'info',
                    IN_PROGRESS: 'warning',
                    OFFER_CREATED: 'primary',
                    IN_DELIVERY_BY_COURIER: 'secondary',
                    IN_DELIVERY_BY_AGENT: 'secondary',
                    CLOSED: 'success',
                    ARCHIVED: 'default',
                };
                const color = statusColors[value] || 'default';
                const label = value?.replace(/_/g, ' ');

                return (
                    <Chip
                        label={label}
                        color={color}
                        size="small"
                        sx={{ fontWeight: 500 }}
                    />
                );
            },
        },
        {
            field: 'createdAt',
            headerName: 'Created',
            width: 150,
            sortable: true,
        },
        {
            field: 'actions',
            headerName: 'Actions',
            width: 250,
            sortable: false,
        },
    ];

    const actions: TableAction[] = [
        {
            label: 'Edit',
            icon: <EditIcon />,
            color: 'primary',
            variant: 'outlined',
            onClick: handleEditClick,
            requirePermission: {
                resource: 'requests',
                action: 'edit',
            },
        },
        {
            label: 'Link',
            icon: <LinkIcon />,
            color: 'secondary',
            variant: 'outlined',
            onClick: handleCopyLink,
            requirePermission: {
                resource: 'requests',
                action: 'read',
            },
        },
    ];

    return (
        <Box className="iauto-admin-page-wrapper">
            <DataTable
                title="Requests"
                icon={<AssignmentIcon />}
                columns={columns}
                data={prepareTableData()}
                actions={actions}
                loading={loading}
                emptyMessage="No Requests Found"
                emptyDescription="There are no requests in the system yet."
                pagination={true}
                pageSize={50}
                sortable={true}
                stickyHeader={true}
                maxHeight={700}
                searchable={true}
                searchPlaceholder="Search by client, operator, or phone..."
                filters={filters}
            />

            <RequestEditModal
                open={editModalOpen}
                onClose={handleEditModalClose}
                requestId={requestIdForEdit}
            />
        </Box>
    );
};

const RequestsProviderHOC: FC<IRequestPage> = memo((props: IRequestPage) => {
    return (
        <UserProvider>
            <RequestProvider>
                <OfferProvider>
                    <RequestsPage {...props} />
                </OfferProvider>
            </RequestProvider>
        </UserProvider>
    );
});

export default RequestsProviderHOC;
